/**
 * Shared text distribution utilities for cover images
 * Consolidates 47+ duplicate implementations into a single configurable utility
 */

export interface TextDistributionConfig {
  /** Maximum width in pixels for text */
  maxWidth: number;
  /** Maximum height in pixels for text (only used for optimal font size calculation) */
  maxHeight?: number;
  /** Font family to use */
  fontFamily?: string;
  /** Line height (default: 1.2) */
  lineHeight?: string;
  /** Letter spacing (optional) */
  letterSpacing?: string;
  /** Text transform (optional, e.g., 'uppercase') */
  textTransform?: string;
  /** Minimum font size (default: 45px) */
  minFontSize?: number;
  /** Maximum font size (default: 120px) */
  maxFontSize?: number;
  /** Use canvas-based measurement (faster) vs DOM-based (default: false = DOM) */
  useCanvas?: boolean;
}

export interface OptimalFontSizeResult {
  fontSize: number;
  lines: string[];
}

/**
 * Distributes text across multiple lines using canvas-based measurement
 * Uses dynamic programming for optimal line breaking
 */
export function distributeTextCanvas(
  text: string,
  config: Pick<
    TextDistributionConfig,
    'maxWidth' | 'fontFamily' | 'minFontSize'
  >,
): string {
  const words = text.split(' ');
  const fontSize = config.minFontSize || 45;
  const fontFamily = config.fontFamily || 'Arial';
  const font = `${fontSize}px ${fontFamily}`;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Failed to get canvas context');
  }
  context.font = font;
  const maxWidth = config.maxWidth;

  function measureTextWidth(text: string): number {
    return context!.measureText(text).width;
  }

  function cost(startIdx: number, endIdx: number) {
    const line = words.slice(startIdx, endIdx + 1).join(' ');
    const lineWidth = measureTextWidth(line);
    if (lineWidth > maxWidth) {
      return Infinity;
    }
    return Math.pow(Math.abs(maxWidth - lineWidth), 2);
  }

  const n = words.length;
  const dp = new Array(n).fill(Infinity);
  const breaks = new Array(n).fill(0);

  for (let i = 0; i < n; i++) {
    for (let j = 0; j <= i; j++) {
      const currentCost = (j === 0 ? 0 : dp[j - 1]) + cost(j, i);
      if (currentCost < dp[i]) {
        dp[i] = currentCost;
        breaks[i] = j;
      }
    }
  }

  const lines = [];
  let idx = n - 1;
  while (idx >= 0) {
    const line = words.slice(breaks[idx], idx + 1).join(' ');
    lines.unshift(line);
    idx = breaks[idx] - 1;
  }

  return lines.join('<br/>');
}

/**
 * Distributes text across multiple lines using DOM-based measurement
 * More accurate for styled text
 */
function distributeTextDOM(
  text: string,
  tester: HTMLDivElement,
  maxWidth: number,
): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  words.forEach((word, index) => {
    tester.innerHTML = word;

    // If even a single word exceeds the maxWidth, it's a special case
    if (tester.clientWidth > maxWidth) {
      if (currentLine.trim()) {
        lines.push(currentLine.trim());
      }
      currentLine = '';
      return;
    }

    tester.innerHTML = currentLine + word;

    if (tester.clientWidth <= maxWidth) {
      currentLine += word + ' ';
    } else {
      lines.push(currentLine.trim());
      currentLine = word + ' ';
    }

    // Append the last line if it's the last word
    if (index === words.length - 1) {
      lines.push(currentLine.trim());
    }
  });

  return lines.filter((line) => line.length > 0);
}

/**
 * Creates a hidden DOM element for text measurement
 */
function createTester(config: TextDistributionConfig): HTMLDivElement {
  const tester = document.createElement('div');
  tester.style.fontFamily = config.fontFamily || 'Arial';
  tester.style.visibility = 'hidden';
  tester.style.position = 'fixed';
  tester.style.left = '-10000px';
  tester.style.top = '-10000px';
  tester.style.whiteSpace = 'nowrap';
  tester.style.lineHeight = config.lineHeight || '1.2';

  if (config.letterSpacing) {
    tester.style.letterSpacing = config.letterSpacing;
  }

  if (config.textTransform) {
    tester.style.textTransform = config.textTransform;
  }

  document.body.appendChild(tester);

  return tester;
}

/**
 * Calculates the optimal font size to fit text within width and height constraints
 * Returns both the font size and distributed lines
 */
export function getOptimalFontSize(
  text: string,
  config: TextDistributionConfig,
): OptimalFontSizeResult {
  const minFontSize = config.minFontSize || 45;
  const maxFontSize = config.maxFontSize || 120;
  const maxHeight = config.maxHeight;

  // Simple height-only optimization (no line distribution)
  if (!maxHeight) {
    return {
      fontSize: minFontSize,
      lines: [text],
    };
  }

  const tester = createTester(config);
  let fontSize = maxFontSize;

  try {
    while (fontSize > minFontSize) {
      tester.style.fontSize = fontSize + 'px';

      // Check if any single word is too long
      const longWords = text.split(' ').filter((word) => {
        tester.innerHTML = word;
        return tester.clientWidth > config.maxWidth;
      });

      if (longWords.length > 0) {
        fontSize--;
        continue;
      }

      const lines = distributeTextDOM(text, tester, config.maxWidth);
      tester.innerHTML = lines.join('<br>');

      if (tester.clientHeight <= maxHeight) {
        return { fontSize, lines };
      }

      fontSize--;
    }

    // Default to minimum font size if we didn't find a fit
    return {
      fontSize: minFontSize,
      lines: distributeTextDOM(text, tester, config.maxWidth),
    };
  } finally {
    document.body.removeChild(tester);
  }
}

/**
 * Simple version that only fits text to height without width distribution
 */
export function getOptimalFontSizeSimple(
  text: string,
  targetWidth: number,
  targetHeight: number,
  fontFamily = 'Arial',
  minFontSize = 45,
  maxFontSize = 120,
): number {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.width = `${targetWidth}px`;
  container.style.height = `${targetHeight}px`;
  container.style.fontFamily = fontFamily;
  container.style.whiteSpace = 'normal';
  container.style.lineHeight = '1.2';
  container.style.overflow = 'hidden';
  container.textContent = text;

  document.body.appendChild(container);

  try {
    let fontSize = maxFontSize;
    let fits = false;

    do {
      container.style.fontSize = `${fontSize}px`;

      if (container.scrollHeight <= targetHeight) {
        fits = true;
      } else {
        fontSize -= 1;
      }
    } while (!fits && fontSize > minFontSize);

    return fontSize < minFontSize ? minFontSize : fontSize;
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Distributes text based on character count (for Variations 5, 6, 7)
 * Uses dynamic programming to find optimal line breaks
 */
export function distributeTextByCharCount(
  text: string,
  maxCharsPerLine: number,
): string {
  const words = text.split(' ');
  const totalChars = words.join(' ').length;
  const totalLines = Math.ceil(totalChars / maxCharsPerLine);
  const idealLineLength = Math.floor(totalChars / totalLines);

  function cost(startIdx: number, endIdx: number) {
    const totalCharsInLine = words.slice(startIdx, endIdx + 1).join(' ').length;
    if (totalCharsInLine > maxCharsPerLine) {
      return Infinity;
    }
    return Math.pow(Math.abs(idealLineLength - totalCharsInLine), 2);
  }

  const n = words.length;
  const dp = new Array(n).fill(Infinity);
  const breaks = new Array(n).fill(0);

  for (let i = 0; i < n; i++) {
    for (let j = 0; j <= i; j++) {
      const currentCost = (j === 0 ? 0 : dp[j - 1]) + cost(j, i);
      if (currentCost < dp[i]) {
        dp[i] = currentCost;
        breaks[i] = j;
      }
    }
  }

  const lines = [];
  let idx = n - 1;
  while (idx >= 0) {
    const line = words.slice(breaks[idx], idx + 1).join(' ');
    lines.unshift(line);
    idx = breaks[idx] - 1;
  }

  return lines.join('<br/>');
}

/**
 * Main unified text distribution function
 * Automatically chooses the best approach based on configuration
 */
export function distributeText(
  text: string,
  config: TextDistributionConfig,
): string | string[] | OptimalFontSizeResult {
  // Canvas-based approach (simple distribution, no font optimization)
  if (config.useCanvas) {
    return distributeTextCanvas(text, config);
  }

  // DOM-based approach with optimal font size calculation
  if (config.maxHeight) {
    return getOptimalFontSize(text, config);
  }

  // Simple DOM-based distribution without font optimization
  const tester = createTester(config);
  try {
    tester.style.fontSize = (config.minFontSize || 45) + 'px';
    return distributeTextDOM(text, tester, config.maxWidth);
  } finally {
    document.body.removeChild(tester);
  }
}
